import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhoneStep } from "./steps/PhoneStep";
import { NameStep } from "./steps/NameStep";
import { OTPStep } from "./steps/OTPStep";
import { invokeTracedFunction } from "@/lib/supabase-traced";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

const STEPS = ['phone', 'otp', 'name'] as const;
type Step = typeof STEPS[number];

export interface SMSSignupData {
  phone: string;
  name: string;
  email?: string;
}

export const SMSSignupFlow = () => {
  const [currentStep, setCurrentStep] = useState<Step>('phone');
  const [signupData, setSignupData] = useState<Partial<SMSSignupData>>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const currentStepIndex = STEPS.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  const handleNext = async (data: Partial<SMSSignupData>) => {
    const updatedData = { ...signupData, ...data };
    setSignupData(updatedData);

    // When phone is submitted, send SMS
    if (currentStep === 'phone' && data.phone) {
      setLoading(true);
      try {
        const { error } = await invokeTracedFunction('send-sms-otp', { 
          phone: data.phone 
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Erro ao enviar SMS",
            description: "Tente novamente ou use verificação por email.",
          });
          setLoading(false);
          return;
        }

        toast({
          title: "SMS enviado!",
          description: "Verifique suas mensagens.",
        });
      } catch (error) {
        logger.error('Failed to send SMS OTP', error);
        toast({
          variant: "destructive",
          title: "Erro ao enviar SMS",
          description: "Tente novamente mais tarde.",
        });
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
    }
  };

  const canGoBack = currentStepIndex > 0 && currentStep !== 'otp';

  const renderStep = () => {
    switch (currentStep) {
      case 'phone':
        return <PhoneStep onNext={handleNext} />;
      case 'otp':
        return <OTPStep onNext={handleNext} identifier={signupData.phone || ''} verificationType="sms" />;
      case 'name':
        return <NameStep onNext={handleNext} initialData={signupData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header com progresso */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-3">
            {canGoBack && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="shrink-0 hover:bg-primary/10 transition-all duration-200"
                  disabled={loading}
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </motion.div>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground font-medium">
                  Passo {currentStepIndex + 1} de {STEPS.length}
                </span>
                <motion.span
                  key={progress}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-sm font-bold text-primary"
                >
                  {Math.round(progress)}%
                </motion.span>
              </div>
              <div className="h-2.5 bg-muted/50 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary via-primary/90 to-primary rounded-full shadow-glow-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ 
                    duration: 0.5, 
                    ease: [0.4, 0, 0.2, 1],
                    type: "spring",
                    stiffness: 100
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo do step */}
      <div className="flex-1 container mx-auto px-4 py-8 max-w-md">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -20, filter: "blur(4px)" }}
            transition={{ 
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="py-4 text-center text-xs text-muted-foreground"
      >
        <p>
          Ao continuar, você concorda com nossos{" "}
          <a href="/terms" className="text-primary hover:underline transition-colors">
            Termos de Uso
          </a>
          {" e "}
          <a href="/privacy" className="text-primary hover:underline transition-colors">
            Política de Privacidade
          </a>
        </p>
      </motion.div>
    </div>
  );
};